import { ref, computed, watch } from 'vue'
import { doc, getDoc, setDoc, deleteField } from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from '@/store/authStore'
import { isWorkspaceEmail } from '@/utils/workspaceUtils'
import {
  pickSchoolSettings,
  splitSettingsPatch,
  planSettingsWrite,
  canWriteDistrictSettings,
  DISTRICT_SETTINGS_WRITE_DENIED,
  NO_SCHOOL_FOR_SETTINGS,
  mergeEffectiveSettings,
  withRemovedSchoolMapKeys,
  buildSchoolSettingsStamp
} from '@/utils/schoolSettings'
import {
  DEFAULT_IMPORT_FLOW_CONFIG,
  resolveImportFlowConfig,
  sanitizeImportFlowConfig
} from '@/utils/importFlowConfig'
import { useSchoolStore } from '@/store/schoolStore'

// Choosing Aeries as the identity source is what hands the importer ownership
// of schedules, so the two fields move together. See setImportSource.
const SCHEDULE_MODE_BY_IDENTITY_SOURCE = {
  app: 'manual',
  'aeries-csv': 'csv',
  'aeries-api': 'api'
}

const DEFAULT_SERVICE_PROVIDERS_DETAILS = [
  { name: 'Speech-Language Therapy', abbreviation: 'SLP' },
  { name: 'Occupational Therapy', abbreviation: 'OT' },
  { name: 'Physical Therapy', abbreviation: 'PT' },
  { name: 'School Counseling', abbreviation: 'SC' },
  { name: 'School-Based Mental Health Services', abbreviation: 'MH', tooltip: 'Mental Health' },
  { name: 'School Psychologist', abbreviation: 'PSYCH', tooltip: 'School Psychologist' },
  {
    name: 'School Psychologist (2)',
    abbreviation: 'PSYCH2',
    tooltip: 'Collaborating school psychologist'
  },
  {
    name: 'County Mental Health Services',
    abbreviation: 'CMH',
    tooltip: 'Mental Health (County)'
  },
  { name: 'Transportation', abbreviation: 'TR' },
  { name: 'Audiology Services', abbreviation: 'AUD' },
  { name: 'Vision Services', abbreviation: 'VI' },
  { name: 'Assistive Technology', abbreviation: 'AT' },
  {
    name: 'Deaf and Hard of Hearing Services',
    abbreviation: 'DHH',
    tooltip: 'Deaf & Hard of Hearing'
  },
  { name: 'Orientation and Mobility', abbreviation: 'O&M' },
  { name: 'Adaptive Physical Education', abbreviation: 'AP', tooltip: 'Adaptive PE' },
  { name: 'Behavioral Intervention Services', abbreviation: 'BIS' },
  { name: 'Health/Nursing Services', abbreviation: 'HN' },
  { name: 'Social Work Services', abbreviation: 'SW' },
  { name: 'Autism Specialist', abbreviation: 'AUT', tooltip: 'Autism Specialist' }
]

export { DEFAULT_SERVICE_PROVIDERS_DETAILS }

/**
 * Code catalog wins for built-in abbreviations (O&M stays Orientation and
 * Mobility; AP / PSYCH / AUT appear even when Firestore still has an older
 * snapshot). Extra stored rows that are not in the code list are kept.
 */
export function mergeStandardProviderCatalog(storedDetails) {
  const stored = Array.isArray(storedDetails) ? storedDetails : []
  const storedByAbbr = new Map()
  for (const item of stored) {
    const abbr = String(item?.abbreviation || '')
      .trim()
      .toLowerCase()
    if (abbr) storedByAbbr.set(abbr, item)
  }

  const merged = DEFAULT_SERVICE_PROVIDERS_DETAILS.map((def) => {
    const existing = storedByAbbr.get(def.abbreviation.toLowerCase())
    return existing ? { ...existing, ...def } : { ...def }
  })

  const known = new Set(
    DEFAULT_SERVICE_PROVIDERS_DETAILS.map((item) => item.abbreviation.toLowerCase())
  )
  for (const item of stored) {
    const abbr = String(item?.abbreviation || '')
      .trim()
      .toLowerCase()
    if (abbr && !known.has(abbr)) merged.push(item)
  }
  return merged
}

const DEFAULT_SETTINGS = {
  grades: [],
  classServices: [
    {
      name: 'SDC',
      subcategories: ['English', 'Math', 'History', 'Science'],
      enabledSubcategories: ['English', 'Math', 'History', 'Science']
    },
    {
      name: 'Co-teach',
      subcategories: ['English', 'Math', 'History', 'Science'],
      enabledSubcategories: ['English', 'Math', 'History', 'Science']
    },
    {
      name: 'RSP',
      subcategories: ['English', 'Math', 'History', 'Science'],
      enabledSubcategories: ['English', 'Math', 'History', 'Science']
    },
    {
      name: 'Other',
      subcategories: ['FA', 'Directed Studies', 'Reading Intervention'],
      enabledSubcategories: ['FA', 'Directed Studies', 'Reading Intervention']
    }
  ],
  sdcSubcategories: [],
  customClassServices: [],
  serviceProviders: ['SLP', 'OT', 'MH'],
  serviceProvidersDetails: DEFAULT_SERVICE_PROVIDERS_DETAILS,
  customServiceProviders: [],
  numPeriods: 7,
  periodLabels: ['1', '2', '3', '4', '5', '6', 'sh'],
  // Unified schedule source mode (single source of truth for the whole
  // import/edit story):
  //   'manual' = student class schedules are hand-edited; importers are tools only.
  //   'csv'    = the CSV/XLSX importer owns schedules; student schedule editor is
  //              read-only and the Aeries API page is soft-disabled.
  //   'api'    = the Aeries API owns schedules; editor read-only and the CSV
  //              importer is soft-disabled.
  // Aide schedules are unaffected by this setting.
  scheduleSource: 'manual',
  // Legacy field kept for backward compatibility (migrated into scheduleSource).
  scheduleSourceOfTruth: 'manual',
  lastScheduleImportAt: null,
  lastScheduleImportBy: null,
  lastSeisImportAt: null,
  lastSeisImportBy: null,
  lastAeriesContactsImportAt: null,
  lastAeriesContactsImportBy: null,
  gmailApi: {
    enabled: false,
    isWorkspace: false,
    isInternalOAuth: false,
    lastCheck: null
  },
  // Google Calendar two-way sync (see Documentation/specs/CALENDAR_SYNC_SPEC.md)
  calendarSync: {
    enabled: false, // master toggle; default on once Workspace is verified
    workspaceConfigured: false, // set when a Workspace domain is configured
    workspaceDomain: '', // e.g. "district.org"
    healthCheckEmail: '', // in-domain user impersonated by the scheduled re-check

    // permission / health
    permissionStatus: 'unknown', // 'ok' | 'missing' | 'unknown'
    lastPermissionCheckAt: null,
    lastPermissionError: null,

    // content / privacy controls
    // Event titles always use student initials (never full names); the full
    // name lives in the event description. No toggle for names in titles.
    defaultMeetingDurationMinutes: 60,
    defaultEventVisibility: 'private'
  },
  // District tenancy Phase 3f — who may create users and pending approval.
  userGovernance: {
    whoCanCreateUsers: 'school_admins_and_sped_chairs',
    requireDistrictApproval: false
  },
  // District tenancy — who may configure sensitive integrations (Aeries API
  // credentials). Stays district-global (not a per-school overlay key). See
  // Developer/ADMIN_DISTRICT_SPLIT_PLAN.md §2 and src/utils/integrationPolicy.js.
  integrationPolicy: {
    whoCanConfigureAeries: 'both'
  },
  // Import source-of-truth flow (school-scoped key; defaults here so the
  // effective settings always carry a complete, valid config).
  importFlow: DEFAULT_IMPORT_FLOW_CONFIG,
  // What teachers can do on a student feedback form. Applied when the form is
  // sent and then frozen on the instance. Admins and SPED chairs set it.
  feedbackTeacherView: {
    allowEditAfterSubmit: true,
    showPeerWrittenAnswers: false,
    showPeerWrittenWhileFilling: false,
    psychShareAssignedOnly: false
  }
}

// Global singleton state — shared across all components that call useAppSettings()
const appSettings = ref({ ...DEFAULT_SETTINGS, importFlow: { ...DEFAULT_IMPORT_FLOW_CONFIG } })
const loading = ref(false)
const error = ref(null)
const settingsVersion = ref(0)
let isInitialized = false
/** Raw app_settings/global (district-wide + legacy single-school fields). */
let globalSettingsCache = { ...DEFAULT_SETTINGS }
/** Per-school overrides from schools/{schoolId}. */
let schoolOverlayCache = {}

function publishEffectiveSettings() {
  // Merge the code catalog first so newly added types (AP, PSYCH, …) exist
  // before the school providerIds filter runs. Otherwise enabling AP in App
  // Settings has no student-form field — Firestore still has the old list.
  applySettings(mergeEffectiveSettings(getGlobalSettingsBase(), schoolOverlayCache))
}

/** Global-only settings (no active-school overlay). For per-school editors. */
export function getGlobalSettingsBase() {
  return {
    ...globalSettingsCache,
    serviceProvidersDetails: mergeStandardProviderCatalog(
      globalSettingsCache.serviceProvidersDetails
    )
  }
}

async function resolveActiveSchoolId() {
  try {
    const schoolStore = useSchoolStore()
    if (schoolStore.currentSchoolId) return schoolStore.currentSchoolId
    await schoolStore.loadSchools()
    if (schoolStore.currentSchoolId) return schoolStore.currentSchoolId
  } catch {
    /* Pinia inactive (tests) */
  }
  const snap = await getDoc(doc(db, 'app_settings', 'global'))
  return snap.exists() ? snap.data().defaultSchoolId || null : null
}

async function loadSchoolOverlay(schoolId) {
  if (!schoolId) return {}
  try {
    const snap = await getDoc(doc(db, 'schools', schoolId))
    return snap.exists() ? pickSchoolSettings(snap.data()) : {}
  } catch (e) {
    console.warn('useAppSettings: could not load school settings:', e?.message || e)
    return {}
  }
}

let schoolWatchRegistered = false
function registerSchoolSwitchWatch() {
  if (schoolWatchRegistered) return
  try {
    const schoolStore = useSchoolStore()
    schoolWatchRegistered = true
    // Must run during component setup (via useAppSettings()), not from async callbacks.
    watch(
      () => schoolStore.currentSchoolId,
      async (newId, oldId) => {
        if (!isInitialized || newId === oldId) return
        schoolOverlayCache = await loadSchoolOverlay(newId)
        publishEffectiveSettings()
      }
    )
  } catch {
    /* Pinia inactive (unit tests) */
  }
}

/**
 * Merge Firestore data with defaults and sanitize known problem fields.
 * Firestore can persist NaN for numeric fields which breaks downstream logic.
 */
function applySettings(data) {
  const merged =
    data && typeof data === 'object' ? { ...DEFAULT_SETTINGS, ...data } : { ...DEFAULT_SETTINGS }

  if (!merged.numPeriods || isNaN(merged.numPeriods) || merged.numPeriods < 1) {
    merged.numPeriods = DEFAULT_SETTINGS.numPeriods
  } else {
    merged.numPeriods = parseInt(merged.numPeriods)
  }

  // Shallow spread above replaces nested objects wholesale; deep-merge the
  // calendarSync block so partially-stored configs keep all default keys.
  merged.calendarSync = { ...DEFAULT_SETTINGS.calendarSync, ...(data?.calendarSync || {}) }

  // Same for importFlow — resolve over defaults and drop invalid/unknown keys
  // (also produces a fresh object, so the shared DEFAULT is never mutated).
  merged.importFlow = resolveImportFlowConfig(data?.importFlow)

  // Migrate legacy scheduleSourceOfTruth → unified scheduleSource. If a stored
  // doc only has the old field set to 'import', treat it as the CSV mechanism
  // (the only import mechanism that existed before the API path).
  if (!data?.scheduleSource && data?.scheduleSourceOfTruth === 'import') {
    merged.scheduleSource = 'csv'
  }
  if (!['manual', 'csv', 'api'].includes(merged.scheduleSource)) {
    merged.scheduleSource = 'manual'
  }

  // Always fold in new built-in types (AP, PSYCH, …) and restore code names
  // for standard abbreviations. A saved "Adaptive PE (O&M)" rename must not
  // hide Adaptive PE as its own slot.
  merged.serviceProvidersDetails = mergeStandardProviderCatalog(merged.serviceProvidersDetails)
  if (!Array.isArray(merged.serviceProviders) || merged.serviceProviders.length === 0) {
    merged.serviceProviders = merged.serviceProvidersDetails
      .map((d) => d?.abbreviation)
      .filter(Boolean)
  }

  // If the school enabled a built-in type that the stored catalog omitted,
  // add it to the abbreviation list the student form reads.
  const enabledIds = Array.isArray(merged.providerIds)
    ? merged.providerIds.map((id) => String(id).toLowerCase()).filter(Boolean)
    : []
  if (enabledIds.length) {
    const enabled = new Set(enabledIds)
    const present = new Set(
      (merged.serviceProviders || []).map((abbr) => String(abbr).toLowerCase())
    )
    const missing = DEFAULT_SERVICE_PROVIDERS_DETAILS.filter(
      (item) =>
        enabled.has(item.abbreviation.toLowerCase()) &&
        !present.has(item.abbreviation.toLowerCase())
    ).map((item) => item.abbreviation)
    if (missing.length) {
      merged.serviceProviders = [...(merged.serviceProviders || []), ...missing]
    }
    merged.serviceProvidersDetails = merged.serviceProvidersDetails.filter((item) =>
      enabled.has(String(item.abbreviation || '').toLowerCase())
    )
  }

  appSettings.value = merged
  settingsVersion.value++
}

export function useAppSettings() {
  const settingsDocRef = doc(db, 'app_settings', 'global')
  registerSchoolSwitchWatch()

  const loadAppSettings = async () => {
    if (loading.value) {
      return new Promise((resolve) => {
        const unwatch = watch(loading, (newLoading) => {
          if (!newLoading) {
            unwatch()
            resolve(appSettings.value)
          }
        })
      })
    }

    if (isInitialized && !error.value) {
      return appSettings.value
    }

    loading.value = true
    error.value = null
    try {
      const docSnap = await getDoc(settingsDocRef)
      const rawGlobal = docSnap.exists() ? docSnap.data() : null
      globalSettingsCache =
        rawGlobal && typeof rawGlobal === 'object'
          ? { ...DEFAULT_SETTINGS, ...rawGlobal }
          : { ...DEFAULT_SETTINGS }
      const schoolId = await resolveActiveSchoolId()
      schoolOverlayCache = await loadSchoolOverlay(schoolId)
      publishEffectiveSettings()
    } catch (e) {
      console.error('useAppSettings: load failed:', e)
      globalSettingsCache = { ...DEFAULT_SETTINGS }
      schoolOverlayCache = {}
      applySettings(null)
    } finally {
      loading.value = false
      isInitialized = true
    }
    return appSettings.value
  }

  const saveAppSettings = async (settings, { requireRemote = false, expectedSchoolId } = {}) => {
    loading.value = true
    error.value = null

    // Sanitize numPeriods before writing — prevents NaN from reaching Firestore.
    // Only touch it when the caller actually includes it, so a partial/slice save
    // (e.g. just the Integrations fields) can't inject a stray default numPeriods.
    if (settings.numPeriods !== undefined) {
      if (!settings.numPeriods || isNaN(settings.numPeriods) || settings.numPeriods < 1) {
        settings.numPeriods = DEFAULT_SETTINGS.numPeriods
      } else {
        settings.numPeriods = parseInt(settings.numPeriods)
      }
    }

    // Merge the incoming (possibly partial) payload over the current in-memory
    // settings so the cache + localStorage backup stay complete even for slice saves.
    const schoolId = await resolveActiveSchoolId()
    if (expectedSchoolId !== undefined && schoolId !== expectedSchoolId) {
      loading.value = false
      throw new Error(
        'The active school changed. Import status was not saved to a different school.'
      )
    }
    let role = null
    try {
      role = useAuthStore().currentUser?.role || null
    } catch {
      /* Pinia inactive (tests) */
    }
    const { schoolPatch, globalPatch, writeSchoolKeysToGlobalFallback } = planSettingsWrite(
      settings,
      { role, schoolId }
    )
    // merge:true keeps omitted nested-map keys; stamp removals with deleteField
    // so SEIS provider-column overrides can actually be deleted. Cache stays
    // on the clean patch (no FieldValue sentinels).
    const firestoreSchoolPatch = withRemovedSchoolMapKeys(
      schoolPatch,
      schoolId ? schoolOverlayCache : globalSettingsCache,
      deleteField()
    )

    // District tenancy: any save of school-scoped settings also forks whatever
    // config keys this school is still silently inheriting from the district
    // defaults, so the school owns its full configuration from the first save
    // onward. Idempotent — keys the school (or this patch) already defines are
    // never touched. Activity keys and SCHOOL_ONLY keys are excluded by
    // buildSchoolSettingsStamp.
    const inheritanceStamp =
      schoolId && Object.keys(schoolPatch).length > 0
        ? buildSchoolSettingsStamp({
            districtDefaults: globalSettingsCache,
            schoolDoc: { ...schoolOverlayCache, ...schoolPatch }
          })
        : {}

    try {
      if (Object.keys(schoolPatch).length > 0 && !schoolId && !writeSchoolKeysToGlobalFallback) {
        throw new Error(NO_SCHOOL_FOR_SETTINGS)
      }
      if (
        Object.keys(globalPatch).length === 0 &&
        Object.keys(schoolPatch).length === 0 &&
        !canWriteDistrictSettings(role)
      ) {
        const leftover = splitSettingsPatch(settings).global
        if (Object.keys(leftover).length > 0) {
          throw new Error(DISTRICT_SETTINGS_WRITE_DENIED)
        }
      }
      if (Object.keys(globalPatch).length > 0) {
        await setDoc(settingsDocRef, globalPatch, { merge: true })
        globalSettingsCache = { ...globalSettingsCache, ...globalPatch }
      }
      if (Object.keys(schoolPatch).length > 0 && schoolId) {
        await setDoc(
          doc(db, 'schools', schoolId),
          { ...inheritanceStamp, ...firestoreSchoolPatch },
          { merge: true }
        )
        schoolOverlayCache = { ...schoolOverlayCache, ...inheritanceStamp, ...schoolPatch }
      } else if (Object.keys(schoolPatch).length > 0 && writeSchoolKeysToGlobalFallback) {
        // Pre-backfill / no registry yet — district-tier only (rules deny school-tier).
        await setDoc(settingsDocRef, firestoreSchoolPatch, { merge: true })
        globalSettingsCache = { ...globalSettingsCache, ...schoolPatch }
      }
      publishEffectiveSettings()

      // Backup key is school-scoped so switching schools can never surface
      // (or restore) another site's settings snapshot.
      const backupKey = `app_settings_backup_${schoolId || 'global'}`
      try {
        localStorage.setItem(backupKey, JSON.stringify(appSettings.value))
      } catch (backupError) {
        console.warn('Failed to save localStorage backup:', backupError)
      }
    } catch (e) {
      error.value = e.message || 'Unknown error'
      if (requireRemote) throw e

      try {
        localStorage.setItem(
          `app_settings_backup_${schoolId || 'global'}`,
          JSON.stringify(appSettings.value)
        )
      } catch (fallbackError) {
        throw new Error('Failed to save settings to both Firestore and localStorage', {
          cause: fallbackError
        })
      }
    } finally {
      loading.value = false
    }
  }

  const resetAppSettings = async () => {
    loading.value = true
    error.value = null
    try {
      // District tenancy: school-scoped defaults reset the ACTIVE SCHOOL's doc,
      // never the shared district document. Only district-tier on a
      // pre-registry project may reset app_settings/global.
      const schoolId = await resolveActiveSchoolId()
      let role = null
      try {
        role = useAuthStore().currentUser?.role || null
      } catch {
        /* Pinia inactive (tests) */
      }
      if (schoolId) {
        const { school, global } = splitSettingsPatch({ ...DEFAULT_SETTINGS })
        if (canWriteDistrictSettings(role)) {
          await setDoc(settingsDocRef, global, { merge: true })
          globalSettingsCache = { ...globalSettingsCache, ...global }
        }
        await setDoc(doc(db, 'schools', schoolId), school, { merge: true })
        schoolOverlayCache = { ...schoolOverlayCache, ...school }
        publishEffectiveSettings()
      } else if (canWriteDistrictSettings(role)) {
        await setDoc(settingsDocRef, { ...DEFAULT_SETTINGS })
        globalSettingsCache = { ...DEFAULT_SETTINGS }
        schoolOverlayCache = {}
        applySettings(null)
      } else {
        throw new Error(NO_SCHOOL_FOR_SETTINGS)
      }
      isInitialized = true
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  // Auto-load on first use (fire-and-forget; snapshot will update the ref)
  if (!isInitialized && !loading.value) {
    loadAppSettings()
  }

  const checkGmailApiStatus = async () => {
    try {
      const authStore = useAuthStore()
      const workspaceDomain = appSettings.value?.calendarSync?.workspaceDomain || ''
      const userEmail = authStore.currentUser?.email || ''
      const isWorkspace = isWorkspaceEmail(userEmail, workspaceDomain)
      const workspaceConfigured = !!String(workspaceDomain || '').trim()

      // Server-side Gmail uses Domain-Wide Delegation — NOT a browser OAuth scope at
      // login. Status comes from Email Delivery config + last test result.
      let emailCfg = { provider: 'none', lastTest: null }
      try {
        const snap = await getDoc(doc(db, 'emailConfig', 'global'))
        if (snap.exists()) emailCfg = snap.data() || emailCfg
      } catch {
        /* non-fatal */
      }

      const serverSideGmail = emailCfg.provider === 'gmail'
      const serverSideVerified = !!emailCfg.lastTest?.ok

      const gmailApiStatus = {
        enabled: serverSideGmail && serverSideVerified,
        isWorkspace,
        workspaceConfigured,
        serverSideGmail,
        serverSideVerified,
        emailProvider: emailCfg.provider || 'none',
        // Legacy key name — means "Gmail provider selected", not OAuth login scope.
        isInternalOAuth: serverSideGmail,
        lastCheck: new Date().toISOString()
      }

      appSettings.value = { ...appSettings.value, gmailApi: gmailApiStatus }
      return gmailApiStatus
    } catch (err) {
      console.error('Failed to check Gmail API status:', err)
      return {
        enabled: false,
        isWorkspace: false,
        workspaceConfigured: false,
        serverSideGmail: false,
        serverSideVerified: false,
        emailProvider: 'none',
        isInternalOAuth: false,
        lastCheck: new Date().toISOString(),
        error: err.message
      }
    }
  }

  // Unified schedule source mode: 'manual' | 'csv' | 'api'.
  const scheduleSource = computed(() => appSettings.value?.scheduleSource || 'manual')
  // True when an importer owns schedules for import/soft-disable. The student
  // form lock is a separate checkbox (importFlow.lockAeriesFieldsInForm).
  const isScheduleImportOwned = computed(() => scheduleSource.value !== 'manual')
  const isCsvImportActive = computed(() => scheduleSource.value === 'csv')
  const isApiImportActive = computed(() => scheduleSource.value === 'api')

  // Import source-of-truth flow — always a complete, valid config.
  const importFlow = computed(() => resolveImportFlowConfig(appSettings.value?.importFlow))

  /**
   * Persist import-flow overrides (school-scoped). Saves the full resolved
   * config so the stored doc always reflects exactly what the Setup UI shows.
   */
  const setImportFlow = async (overrides) => {
    const next = { ...importFlow.value, ...sanitizeImportFlowConfig(overrides) }
    await saveAppSettings({ importFlow: next })
  }

  /**
   * Set the unified schedule source mode. Keeps the legacy scheduleSourceOfTruth
   * field in sync so any older reader still behaves correctly.
   * @param {'manual'|'csv'|'api'} mode
   */
  const setScheduleSource = async (mode) => {
    if (!['manual', 'csv', 'api'].includes(mode)) return
    await saveAppSettings({
      scheduleSource: mode,
      scheduleSourceOfTruth: mode === 'manual' ? 'manual' : 'import'
    })
  }

  /**
   * Set the import source of truth together with the schedule mode it implies.
   *
   * The two are one decision — picking Aeries as the source is what hands the
   * importer ownership of schedules — but they live in different fields, and
   * more than one screen sets them. Writing them separately lets a failed
   * second call (or a screen that only knows about one) leave Import Setup
   * describing a source the importers are not actually using. One save keeps
   * every surface telling the same story.
   *
   * @param {'app'|'aeries-csv'|'aeries-api'} identitySource
   */
  const setImportSource = async (identitySource) => {
    const mode = SCHEDULE_MODE_BY_IDENTITY_SOURCE[identitySource]
    if (!mode) return
    await saveAppSettings({
      importFlow: { ...importFlow.value, ...sanitizeImportFlowConfig({ identitySource }) },
      scheduleSource: mode,
      scheduleSourceOfTruth: mode === 'manual' ? 'manual' : 'import'
    })
  }

  return {
    appSettings,
    settingsVersion,
    loadAppSettings,
    saveAppSettings,
    resetAppSettings,
    checkGmailApiStatus,
    scheduleSource,
    setScheduleSource,
    setImportSource,
    isScheduleImportOwned,
    isCsvImportActive,
    isApiImportActive,
    importFlow,
    setImportFlow,
    loading,
    error,
    DEFAULT_SERVICE_PROVIDERS_DETAILS
  }
}
